import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import { useExitIntent, dismissExitIntent } from "@/hooks/use-exit-intent";
import { useState } from "react";
import { toast } from "sonner";

const bookingUrl = "/book";
const GOOGLE_DOCS_URL = "https://docs.google.com/document/d/1rd6cEcH6DZOcjYC0g0zHkIvDnppXQU3cObiKSlHx9Ko/copy";

interface ExitIntentPopupProps {
  preHeading?: string | null;
  title?: string;
  description?: string;
  ctaText?: string;
  redirectUrl?: string;
  variant?: "button" | "form";
  source?: string;
}

export function ExitIntentPopup({
  preHeading = "Summer New Site Special",
  title = "Get Your Custom Website For Only $99",
  description = "A complete, multi-page custom website, launched in 3 days. Regularly starting at $2k. Book a free call to claim the Summer New Site Special now.",
  ctaText = "Book a Free Call to Claim Now",
  redirectUrl = bookingUrl,
  variant = "button",
  source = "exit-popup",
}: ExitIntentPopupProps) {
  const { open, setOpen } = useExitIntent();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Temporarily hidden on all pages per request.
  return null;

  const handleOpenChange = (next: boolean) => {
    if (!next) dismissExitIntent();
    setOpen(next);
  };

  const handleCta = () => {
    dismissExitIntent();
    setOpen(false);
    if (typeof window !== "undefined") {
      window.location.href = redirectUrl;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (trimmedName.length < 1) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      toast.error("Please enter a valid email.");
      return;
    }

    const destination = redirectUrl || GOOGLE_DOCS_URL;
    // Open synchronously within the user gesture so popup blockers allow it.
    // Note: do NOT pass "noopener" — that makes window.open() return null and
    // we'd lose the handle needed to redirect the tab after the fetch.
    const newTab = typeof window !== "undefined"
      ? window.open("about:blank", "_blank")
      : null;


    setSubmitting(true);
    try {
      const res = await fetch("/api/public/contact-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          business: "",
          message: `Free Website Page Framework request from ${source}`,
          formType: "free_framework",
          pagePath: typeof window !== "undefined" ? window.location.pathname : null,
        }),
      });

      if (!res.ok) {
        throw new Error("Submission failed");
      }

      dismissExitIntent();
      setOpen(false);
      setName("");
      setEmail("");
      toast.success("Success! Opening your free framework...");
      if (newTab && !newTab.closed) {
        try { newTab.opener = null; } catch { /* noop */ }
        newTab.location.href = destination;

      } else if (typeof window !== "undefined") {
        // Fallback: popup was blocked — navigate current tab.
        window.location.href = destination;
      }
    } catch (err) {
      if (newTab && !newTab.closed) newTab.close();
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg border-border bg-card text-foreground">
        <div className="flex flex-col items-center text-center px-2 py-2">
          {preHeading !== null && preHeading !== undefined && preHeading !== "" && (
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {preHeading}
            </div>
          )}
          <DialogTitle className="mt-4 text-3xl font-bold tracking-tight">
            {title}
          </DialogTitle>
          <DialogDescription className="mt-4 text-base text-foreground/85">
            {description}
          </DialogDescription>

          {variant === "form" ? (
            <form onSubmit={handleSubmit} className="mt-6 w-full space-y-4 text-left">
              <div className="space-y-2">
                <Label htmlFor="exit-popup-name">Name</Label>
                <Input
                  id="exit-popup-name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exit-popup-email">Email</Label>
                <Input
                  id="exit-popup-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full rounded-none"
              >
                {submitting ? "Submitting..." : ctaText}
              </Button>
            </form>
          ) : (
            <Button onClick={handleCta} size="lg" className="mt-6 w-full">
              {ctaText}
            </Button>
          )}
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="mt-4 text-xs text-muted-foreground underline-offset-4 hover:underline"
          >
            No thanks
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
